/* eslint-disable mastery/named-colors */
import {
  GrayMap,
  GrayValue,
  Theme as ThemeInterface,
  ThemeProvider,
} from '@emotion/react';
import { useFlagMe449354EnableDarkModeButtonColorUpdates } from '@generated/flags/ME-449354-enable-dark-mode-button-color-updates';
import { FC, PropsWithChildren, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  addButtonDarkModeClass,
  removeButtonDarkModeClass,
  useIsDarkTheme,
} from '@utils/theme';
import { PRIVATE_colors, PRIVATE_dark_colors, newColors } from './colors';
import {
  CODE_FONT,
  MAIN_FONT,
  TABLE_ROW_BORDER_HEIGHT,
  TABLE_ROW_MIN_HEIGHT as foo,
} from './constants';
import { semanticMap } from './newBaseColors';
import { appThemeContract } from './pallette.css';

const { gray, selectedRow, card, hover } = appThemeContract;

const tableCellBackground = gray.white as string;
const tableCellBorderColor = gray[80] as string;

const tableSelectedRowBackground = selectedRow?.background as string;
const cardBackground = card?.background;

const hoverBackground = hover?.background as string;

const box = {
  borderColor: gray[80],
};

const TABLE_ROW_MIN_HEIGHT = foo - 1;

/** This is a private and internal object. Use the `@hooks/useTheme` React hook instead. */
export const PRIVATE_themeObj: ThemeInterface = {
  theme: 'light',
  colors: PRIVATE_colors,
  newColors,
  hover: {
    background: hoverBackground,
  },
  surface: {
    lowest: semanticMap.neutral[100],
    low: semanticMap.neutral[97],
    container: semanticMap.neutral[95],
    base: semanticMap.neutral[92],
    containerHigh: semanticMap.neutral[91],
    highest: semanticMap.neutral[90],
    highlight: appThemeContract?.primary?.[40],
  },
  text: {
    // implied background of our standard card components (e.g. Modals, Dialogs, etc)
    warning: appThemeContract?.warning?.[50],
    accent: appThemeContract?.violet?.[40],
    primary: appThemeContract?.primary?.[40],
    standard: appThemeContract?.gray?.[30],
    error: appThemeContract?.error?.[40],
    // Semantic text tokens
    secondary: appThemeContract?.gray?.[60], // Subtitles, descriptions
    tertiary: appThemeContract?.gray?.[70], // Captions, hints
    inverse: appThemeContract?.gray?.white, // Text on inverse backgrounds
    disabled: appThemeContract?.gray?.[60], // Disabled text
    link: {
      default: appThemeContract?.primary?.[50], // Links, actions
      hover: appThemeContract?.primary?.[40], // Link hover state
      visited: appThemeContract?.violet?.[50], // Visited links
    },
    status: {
      info: appThemeContract?.primary?.[40], // Info message headers
      success: appThemeContract?.success?.[40], // Success message text
      warning: appThemeContract?.warning?.[50], // Warning message text
      error: appThemeContract?.error?.[40], // Error message text
    },
  },
  icon: {
    iconPrimary: appThemeContract?.primary?.[40], // Default icons
    iconSecondary: appThemeContract?.gray?.[60], // Subtle icons
    iconInverse: appThemeContract?.gray?.white, // Icons on dark backgrounds
    iconDisabled: appThemeContract?.gray?.[70], // Disabled icons
    iconInfo: appThemeContract?.primary?.[50], // Info icons
    iconSuccess: appThemeContract?.success?.[50], // Success icons
    iconWarning: appThemeContract?.warning?.[60], // Warning icons
    iconError: appThemeContract?.error?.[50], // Error icons
  },
  gray: gray as unknown as GrayMap,
  button: {
    background: gray.white,
  },
  box,
  card: {
    background: cardBackground as unknown as GrayValue,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.03)',
    borderRadius: 0,
  },
  chip: {
    text: appThemeContract?.gray?.[60],
    border: appThemeContract?.gray?.[60],
    required: {
      background: appThemeContract?.violet?.[95],
      text: appThemeContract?.violet?.[30],
    },
    active: {
      background: appThemeContract?.primary?.[95],
      text: appThemeContract?.primary?.[30],
    },
  },
  contextMenu: {
    background: gray.white,
  },
  fonts: {
    main: MAIN_FONT,
    code: CODE_FONT,
  },
  formElement: {
    background: gray.white,
    borderColor: box.borderColor,
    focused: {
      border: `1px solid ${PRIVATE_colors.primary}`,
    },
    hasError: {
      border: `1px solid ${PRIVATE_colors.error}`,
    },
    hasWarning: {
      border: `1px solid ${PRIVATE_colors.warningDark}`,
    },
    readOnly: {
      background: gray[95],
    },
  },
  hr: {
    background: gray[90],
  },
  dividerStyle: {
    borderRight: `2px solid ${gray[90]}`,
    boxShadow: 'none',
  },
  dialog: {
    overlay: {
      background: 'rgba(0,0,0,.75)',
    },
    content: {
      background: gray.white,
    },
  },
  shell: {
    content: {
      background: 'var(--body-color)',
    },
  },
  table: {
    tbody: {
      backgroundImage: `linear-gradient(to bottom,
        ${tableCellBackground} 0px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT * 2
        }px,
        ${tableCellBackground} ${TABLE_ROW_MIN_HEIGHT}px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 2
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 2
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 3
        }px,
        ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 3
        }px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 3
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 3
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 4
        }px,
        ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 4
        }px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 4
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 4
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5
        }px)`,
      backgroundSize: `${
        TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5
      }px ${TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5}px`,
      backgroundAttachment: 'local',
    },
    title: {
      background: gray.white,
    },
    header: {
      background: gray[90],
      color: '#17191d',
    },
    row: {
      selectedRowBackground: `${tableSelectedRowBackground}`,
    },
    cell: {
      background: tableCellBackground,
      borderTop: `1px solid ${tableCellBorderColor}`,
      alternateBackground: tableCellBackground,
      hoverBackground: gray[95],
    },
  },
  tooltip: {
    background: gray[95],
    color: PRIVATE_colors.text,
    border: `1px solid ${gray[80]}`,
  },
  sidebar: {
    background: cardBackground,
    link: {
      marginLeft: '-8px',
      fontSize: 14,
      padding: '8px',
      borderRadius: '3px',
      display: 'block',
      color: gray[10],
      textDecoration: 'none',
    },
    active: {
      background: '#e9f2fc',
      color: PRIVATE_colors.primary,
      fontWeight: 'bold',
    },
  },
};

const darkModeTheme: ThemeInterface = {
  theme: 'dark',
  colors: PRIVATE_dark_colors,
  newColors,
  hover: {
    background: hoverBackground,
  },
  surface: {
    lowest: appThemeContract.gray[0],
    low: appThemeContract.gray[97],
    base: appThemeContract.gray[12],
    container: appThemeContract.gray[17],
    containerHigh: appThemeContract.gray[20],
    highest: appThemeContract.gray[30],
    highlight: hoverBackground,
  },
  text: {
    warning: appThemeContract?.warning?.[80],
    accent: appThemeContract?.violet?.[80],
    primary: appThemeContract?.primary?.[80],
    standard: appThemeContract?.gray?.[10],
    error: appThemeContract?.error?.[70],
    // Semantic text tokens for dark mode
    secondary: appThemeContract?.gray?.[70], // Subtitles, descriptions (lighter in dark mode)
    tertiary: appThemeContract?.gray?.[60], // Captions, hints (lighter in dark mode)
    inverse: appThemeContract?.gray?.[10], // Text on inverse backgrounds
    disabled: appThemeContract?.gray?.[40], // Disabled text (lighter in dark mode)
    link: {
      default: appThemeContract?.primary?.[80], // Links, actions (lighter in dark mode)
      hover: appThemeContract?.primary?.[70], // Link hover state
      visited: appThemeContract?.violet?.[80], // Visited links
    },
    status: {
      info: appThemeContract?.primary?.[80], // Info message headers
      success: appThemeContract?.success?.[70], // Success message text (lighter in dark mode)
      warning: appThemeContract?.warning?.[10], // Warning message text (lighter in dark mode)
      error: appThemeContract?.error?.[10], // Error message text (lighter in dark mode)
    },
  },
  icon: {
    iconPrimary: appThemeContract?.primary?.[80], // Default icons (lighter in dark mode)
    iconSecondary: appThemeContract?.gray?.[70], // Subtle icons (lighter in dark mode)
    iconInverse: appThemeContract?.gray?.[10], // Icons on light backgrounds in dark mode
    iconDisabled: appThemeContract?.gray?.[50], // Disabled icons (lighter in dark mode)
    iconInfo: appThemeContract?.primary?.[80], // Info icons (lighter in dark mode)
    iconSuccess: appThemeContract?.success?.[60], // Success icons (lighter in dark mode)
    iconWarning: appThemeContract?.warning?.[70], // Warning icons (lighter in dark mode)
    iconError: appThemeContract?.error?.[70], // Error icons (lighter in dark mode)
  },
  gray: gray as unknown as GrayMap,
  button: {
    background: gray.white,
  },
  box: {
    borderColor: gray[80],
  },
  card: {
    background: cardBackground as unknown as GrayValue,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.03)',
    borderRadius: 0,
  },
  chip: {
    text: appThemeContract?.gray?.[70],
    border: appThemeContract?.gray?.[70],
    required: {
      background: appThemeContract?.violet?.[80],
      text: appThemeContract?.violet?.[20],
    },
    active: {
      background: appThemeContract?.primary?.[80],
      text: appThemeContract?.primary?.[20],
    },
  },
  contextMenu: {
    background: '#1B1B1F',
  },
  fonts: {
    main: MAIN_FONT,
    code: CODE_FONT,
  },
  formElement: {
    background: `#46464A !important`,
    borderColor: box.borderColor, // gray[30] in dark mode
    focused: {
      border: `1px solid ${PRIVATE_dark_colors?.primary}`,
    },
    hasError: {
      border: `1px solid ${PRIVATE_dark_colors?.error}`,
    },
    hasWarning: {
      border: `1px solid ${PRIVATE_dark_colors?.warning}`,
    },
    readOnly: {
      background: '#303033',
    },
  },
  hr: {
    background: gray[90],
  },
  dividerStyle: {
    borderRight: `2px solid ${gray[90]}`,
    boxShadow: 'none',
  },
  dialog: {
    overlay: {
      background: 'rgba(0,0,0,.75)',
    },
    content: {
      background: gray.white,
    },
  },
  shell: {
    content: {
      background: 'var(--body-color-theme-dark)',
    },
  },
  table: {
    tbody: {
      backgroundImage: `linear-gradient(to bottom,
        ${tableCellBackground} 0px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT + TABLE_ROW_BORDER_HEIGHT * 2
        }px,
        ${tableCellBackground} ${TABLE_ROW_MIN_HEIGHT}px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 2
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 2
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 3
        }px,
        ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 2 + TABLE_ROW_BORDER_HEIGHT * 3
        }px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 3
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 3
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 4
        }px,
        ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 3 + TABLE_ROW_BORDER_HEIGHT * 4
        }px, ${tableCellBackground} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 4
        }px,
        ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 4
        }px, ${tableCellBorderColor} ${
          TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5
        }px)`,
      backgroundSize: `${
        TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5
      }px ${TABLE_ROW_MIN_HEIGHT * 4 + TABLE_ROW_BORDER_HEIGHT * 5}px`,
      backgroundAttachment: 'local',
    },
    title: {
      background: gray.white,
    },
    header: {
      background: '#2F3136',
      color: '#17191d',
    },
    row: {
      selectedRowBackground: `${tableSelectedRowBackground}`,
    },
    cell: {
      background: tableCellBackground,
      borderTop: `1px solid ${tableCellBorderColor}`,
      alternateBackground: tableCellBackground,
      hoverBackground: gray[95],
    },
  },
  tooltip: {
    background: gray[95],
    color: PRIVATE_colors.text,
    border: `1px solid ${gray[80]}`,
  },
  sidebar: {
    background: cardBackground,
    link: {
      marginLeft: '-8px',
      fontSize: 14,
      padding: '8px',
      borderRadius: '3px',
      display: 'block',
      color: gray[10],
      textDecoration: 'none',
    },
    active: {
      background: `#46464A !important`,
      color: PRIVATE_dark_colors?.primary,
      fontWeight: 'bold',
    },
  },
};

/** You should not need this component in src/views. It's already set up for you. */
export const Theme: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const isDark = useIsDarkTheme();
  const isButtonDarkModeEnabled =
    useFlagMe449354EnableDarkModeButtonColorUpdates();

  // Manage button-dark-mode-enabled class based on feature flag
  useEffect(() => {
    if (isButtonDarkModeEnabled) {
      addButtonDarkModeClass();
    } else {
      removeButtonDarkModeClass();
    }

    return () => {
      removeButtonDarkModeClass();
    };
  }, [isButtonDarkModeEnabled]);

  return (
    <ThemeProvider theme={isDark ? darkModeTheme : PRIVATE_themeObj}>
      {children}
    </ThemeProvider>
  );
};
